import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useColumnDistinct } from './queries';

interface Props {
  moduleKey: string;
  column: string;
  values: string[];
  placeholder?: string;
  onChange: (values: string[]) => void;
  // Other fields' current picks — this column's options cascade (narrow) to
  // match them (the hook drops this same column so it never limits itself).
  cascade?: Record<string, string[]>;
  // Sort the option list descending (e.g. newest Job IDs first).
  descending?: boolean;
}

/**
 * Multi-select checkbox combo for the "Query Samples" dialog. Looks like a
 * combobox (selection summary + chevron); on open it lazily loads the column's
 * distinct values (type to filter the server query) and lets the user tick
 * several. A "Select all / Clear" toolbar tops the list. Selections feed the
 * module's per-column IN-list filter.
 *
 * The popup is portaled to <body> behind a transparent full-screen backdrop.
 * The backdrop both dismisses the popup on an outside click AND — because an
 * open dropdown visually covers the fields beneath it — forwards that click to
 * whichever combo trigger sits under it, so the user can jump straight from one
 * filter to the next without closing the whole dialog first.
 */
export function QueryCombo({ moduleKey, column, values, placeholder, onChange, cascade, descending }: Props) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [q, setQ] = useState('');
  /** Keyboard-cursor row index while the popup is open (‑1 = no cursor yet). */
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 200 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  const openPanel = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + window.scrollY + 2, left: r.left + window.scrollX, width: r.width });
    setInput('');
    setQ('');
    setOpen((o) => !o);
  };

  useEffect(() => {
    const t = setTimeout(() => setQ(input), 250);
    return () => clearTimeout(t);
  }, [input]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Month columns (MSR) always list ALL 12 months in CALENDAR order — mirrors
  // the legacy ALPACA C# Month enum declaration order, never the alphabetical
  // DB-distinct order.  The list is fixed, so the distinct API call is skipped
  // entirely (open=false) and the hardcoded list is filtered client-side.
  // (Merged team package 2026-07-02 — MSR month-sort fix.)
  const ALL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const isMonthCol = column === 'month';

  const { data: rawOpts = [], isFetching: apiFetching } = useColumnDistinct(
    moduleKey, column, q, null, isMonthCol ? false : open, cascade, descending,
  );

  const opts = isMonthCol
    ? ALL_MONTHS.filter((m) => !q || m.toLowerCase().includes(q.toLowerCase()))
    : rawOpts;
  const isFetching = isMonthCol ? false : apiFetching;

  const toggle = (v: string) => {
    const set = new Set(values);
    if (set.has(v)) set.delete(v); else set.add(v);
    onChange([...set]);
  };

  // Reset the keyboard cursor when the popup opens or the (debounced) filter changes,
  // so the first ↓ lands on the top matching row.
  useEffect(() => { setActive(-1); }, [open, q]);
  // Keep the cursor row scrolled into view as ↑/↓ move it.
  useEffect(() => {
    if (!open || active < 0) return;
    popRef.current?.querySelector('.qs-combo-opt-active')?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  // Keyboard list navigation from the filter box (mouse-free, ADR-0013): ↓/↑ move a
  // cursor row (Home/End jump), Enter TOGGLES the cursor option and STAYS open (this is
  // a multi-select checkbox combo — like CheckedCombo/multi LookupCombo), Esc closes and
  // returns to the trigger. stopPropagation so the AppShell spatial nav / global hotkeys
  // never act on the same key (the popup is portaled but React events bubble the tree).
  const onListKey = (e: ReactKeyboardEvent) => {
    if (e.key === 'ArrowDown')      { e.preventDefault(); e.stopPropagation(); setActive(a => (a < 0 ? 0 : Math.min(opts.length - 1, a + 1))); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); e.stopPropagation(); setActive(a => (a < 0 ? opts.length - 1 : Math.max(0, a - 1))); }
    else if (e.key === 'Home')      { e.preventDefault(); e.stopPropagation(); setActive(opts.length ? 0 : -1); }
    else if (e.key === 'End')       { e.preventDefault(); e.stopPropagation(); setActive(opts.length - 1); }
    else if (e.key === 'Enter')     { e.preventDefault(); e.stopPropagation(); const v = opts[active]; if (v !== undefined) toggle(v); }
    else if (e.key === 'Escape')    { e.preventDefault(); e.stopPropagation(); setOpen(false); btnRef.current?.focus(); }
  };

  // "Select all" adds every currently-loaded option (respects the active
  // type-to-filter query); "Clear" empties the selection.
  const allSelected = opts.length > 0 && opts.every((v) => values.includes(v));
  const selectAll = () => onChange([...new Set([...values, ...opts])]);

  // Outside-click on the backdrop: close this popup, and if the click landed on
  // another field's combo trigger (hidden beneath the open dropdown), open that
  // one in the same gesture.
  const onBackdrop = (e: ReactMouseEvent<HTMLDivElement>) => {
    const bd = e.currentTarget;
    bd.style.pointerEvents = 'none';
    const under = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
    bd.style.pointerEvents = '';
    setOpen(false);
    const trigger = under?.closest('[data-qcombo]') as HTMLElement | null;
    if (trigger && trigger !== btnRef.current) trigger.click();
  };

  const summary = values.length === 0 ? (placeholder ?? 'Select…')
    : values.length === 1 ? values[0]
    : `${values.length} selected`;

  return (
    <>
      <button ref={btnRef} type="button" className="qs-combo" data-qcombo={column} onClick={openPanel}>
        <span className={values.length ? 'qs-combo-val' : 'qs-combo-ph'}>{summary}</span>
        {values.length
          ? <i className="ti ti-x qs-combo-clear" onClick={(e) => { e.stopPropagation(); onChange([]); }} />
          : <i className="ti ti-chevron-down qs-combo-chev" />}
      </button>

      {open && createPortal(
        <>
          <div className="qs-combo-backdrop" onMouseDown={onBackdrop} />
          <div ref={popRef} className="qs-combo-pop"
            style={{ top: pos.top, left: pos.left, minWidth: Math.max(200, pos.width) }}>
            <div className="qs-combo-search">
              <i className="ti ti-search" />
              <input autoFocus value={input} placeholder="Type to filter…"
                onChange={(e) => setInput(e.target.value)} onKeyDown={onListKey} />
            </div>
            {!isFetching && opts.length > 0 && (
              <div className="qs-combo-tools">
                <button type="button" className="qs-combo-ftr-btn"
                  onClick={selectAll} disabled={allSelected}>Select all</button>
                <button type="button" className="qs-combo-ftr-btn red"
                  onClick={() => onChange([])} disabled={values.length === 0}>Clear</button>
                <span style={{ flex: 1 }} />
                <button type="button" className="qs-combo-ftr-ok"
                  onClick={() => setOpen(false)}>OK</button>
              </div>
            )}
            <div className="qs-combo-list">
              {isFetching && <div className="qs-combo-empty">Loading…</div>}
              {!isFetching && opts.length === 0 && <div className="qs-combo-empty">No values</div>}
              {opts.map((v, i) => (
                <label key={v} className={`qs-combo-opt${values.includes(v) ? ' sel' : ''}${i === active ? ' qs-combo-opt-active' : ''}`} title={v}>
                  <input type="checkbox" checked={values.includes(v)} onChange={() => toggle(v)} />
                  <span>{v}</span>
                </label>
              ))}
              {!isFetching && opts.length >= 500 && (
                <div className="qs-combo-empty">First 500 — type to refine</div>
              )}
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}
